import { formatCurrency, formatNumber } from '@angular/common';
import {
  afterNextRender,
  Component,
  ElementRef,
  inject,
  Injector,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  signal,
  SimpleChanges,
  viewChild,
} from '@angular/core';
import { ReactiveFormsModule, UntypedFormControl } from '@angular/forms';
import { Subscription } from 'rxjs';

@Component({
  standalone: true,
  selector: 'ct-lib-salary-input',
  templateUrl: './salary-input.component.html',
  styleUrls: ['./salary-input.component.scss'],
  imports: [ReactiveFormsModule],
})
export class SalaryInputComponent implements OnInit, OnChanges, OnDestroy {
  @Input() control: UntypedFormControl = new UntypedFormControl(null, []);
  @Input() placeholder = '';
  @Input() interval = '';
  /** ISO 4217 code, e.g. EUR, USD */
  @Input() currencyCode = 'EUR';
  /**
   * When false, the formatted field shows only the number (locale-formatted), no currency symbol.
   * Use when the currency is shown separately next to the control (e.g. job editor).
   */
  @Input() displayCurrencySymbol = true;

  protected readonly isEditing = signal(false);
  protected readonly formattedPreviewText = signal('');

  private readonly injector = inject(Injector);
  private readonly salaryNumberInput = viewChild<ElementRef<HTMLInputElement>>(
    'salaryNumberInput'
  );
  private controlValueSubscription?: Subscription;

  setValue(salary: number | null): void {
    if (salary === null || salary === undefined) {
      this.control.setValue(null);
      this.control.markAsPristine();
      this.control.updateValueAndValidity();
      this.refreshPreview();
      return;
    }
    if (!salary) {
      return;
    }
    this.control.setValue(Math.trunc(salary));
    this.refreshPreview();
  }

  ngOnInit(): void {
    this.coerceControlToInteger();
    this.refreshPreview();
    this.controlValueSubscription = this.control.valueChanges.subscribe(() => {
      if (!this.isEditing()) {
        this.refreshPreview();
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    const isBootstrap = Object.values(changes).every((change) => change.firstChange);
    if (isBootstrap) {
      return;
    }
    if (changes['currencyCode'] || changes['displayCurrencySymbol']) {
      if (!this.isEditing()) {
        this.refreshPreview();
      }
    }
  }

  ngOnDestroy(): void {
    this.controlValueSubscription?.unsubscribe();
  }

  onPreviewFocus(): void {
    this.isEditing.set(true);
    afterNextRender(
      () => {
        const numberInputElement = this.salaryNumberInput()?.nativeElement;
        numberInputElement?.focus();
        numberInputElement?.select();
      },
      { injector: this.injector }
    );
  }

  onNumberBlur(): void {
    this.coerceControlToInteger();
    this.isEditing.set(false);
    this.refreshPreview();
  }

  /** Block `.`, `,`, scientific notation and sign — only digits while editing. */
  onSalaryKeydown(event: KeyboardEvent): void {
    if (event.ctrlKey || event.metaKey || event.altKey) {
      return;
    }
    const allowedNonCharKeys = new Set([
      'Backspace',
      'Delete',
      'Tab',
      'Escape',
      'Enter',
      'ArrowLeft',
      'ArrowRight',
      'ArrowUp',
      'ArrowDown',
      'Home',
      'End',
    ]);
    if (allowedNonCharKeys.has(event.key)) {
      return;
    }
    if (event.key.length === 1 && event.key >= '0' && event.key <= '9') {
      return;
    }
    event.preventDefault();
  }

  onSalaryPaste(event: ClipboardEvent): void {
    event.preventDefault();
    const inputElement = event.target as HTMLInputElement;
    const pastedText = event.clipboardData?.getData('text/plain') ?? '';
    const pasteDigits = pastedText.replace(/\D/g, '');
    const selectionStart = inputElement.selectionStart ?? 0;
    const selectionEnd = inputElement.selectionEnd ?? 0;
    const digitsBeforeSelection = inputElement.value
      .slice(0, selectionStart)
      .replace(/\D/g, '');
    const digitsAfterSelection = inputElement.value.slice(selectionEnd).replace(/\D/g, '');
    const mergedDigitString = digitsBeforeSelection + pasteDigits + digitsAfterSelection;
    if (mergedDigitString === '') {
      this.control.setValue(null);
    } else {
      const truncatedSalary = Math.trunc(Number(mergedDigitString));
      if (!Number.isFinite(truncatedSalary)) {
        return;
      }
      this.control.setValue(truncatedSalary);
    }
    this.control.updateValueAndValidity();
    const caretIndex = digitsBeforeSelection.length + pasteDigits.length;
    afterNextRender(
      () => {
        inputElement.setSelectionRange(caretIndex, caretIndex);
      },
      { injector: this.injector }
    );
  }

  private refreshPreview(): void {
    const value = this.control.value;
    if (value === null || value === undefined || value === '') {
      this.formattedPreviewText.set('');
      return;
    }
    const truncatedSalary = Math.trunc(Number(value));
    if (!Number.isFinite(truncatedSalary)) {
      this.formattedPreviewText.set('');
      return;
    }
    const formatted = this.displayCurrencySymbol
      ? formatCurrency(
          truncatedSalary,
          'de-DE',
          this.currencyCode,
          'symbol',
          '1.0-0'
        )
      : formatNumber(truncatedSalary, 'de-DE', '1.0-0');
    const intervalSuffix = this.interval?.trim() ? ` ${this.interval.trim()}` : '';
    this.formattedPreviewText.set(`${formatted}${intervalSuffix}`);
  }

  private coerceControlToInteger(): void {
    const value = this.control.value;
    if (value === null || value === undefined || value === '') {
      this.control.setValue(null, { emitEvent: false });
      this.control.updateValueAndValidity();
      return;
    }
    if (typeof value === 'string' && value.trim() === '') {
      this.control.setValue(null, { emitEvent: false });
      this.control.updateValueAndValidity();
      return;
    }
    const numericValue =
      typeof value === 'string'
        ? Number(value.replace(/\D/g, ''))
        : Number(value);
    if (!Number.isFinite(numericValue)) {
      this.control.setValue(null);
      this.control.updateValueAndValidity();
      return;
    }
    const truncatedSalary = Math.trunc(numericValue);
    if (truncatedSalary !== numericValue) {
      this.control.setValue(truncatedSalary, { emitEvent: false });
    } else if (typeof value === 'string') {
      this.control.setValue(truncatedSalary, { emitEvent: false });
    }
    this.control.updateValueAndValidity();
  }
}
